import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Coins, TriangleAlert, Plus, Infinity as InfinityIcon } from "lucide-react";

// Subtle, informative credit-balance footer shown at the end of a journey stage.
// Turns urgent (amber) only when the balance drops below 10.
export function CreditFooter({ className = "" }) {
  const navigate = useNavigate();
  const { credits } = useAuth();
  if (!credits) return null;

  if (credits.unlimited) {
    return (
      <div data-testid="credit-footer"
        className={`flex items-center gap-2.5 rounded-2xl border border-border bg-secondary/40 px-5 py-4 text-sm text-muted-foreground ${className}`}>
        <InfinityIcon size={17} className="shrink-0 text-primary" />
        <span>You have <span className="font-semibold text-foreground">unlimited</span> credits.</span>
      </div>
    );
  }

  const balance = credits.balance ?? 0;
  const urgent = balance < 10;

  return (
    <div data-testid="credit-footer"
      className={`flex flex-col gap-3 rounded-2xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between ${
        urgent ? "border-amber-300 bg-amber-50" : "border-border bg-secondary/40"
      } ${className}`}>
      <div className="flex items-start gap-2.5">
        {urgent
          ? <TriangleAlert size={18} className="mt-0.5 shrink-0 text-amber-600" />
          : <Coins size={18} className="mt-0.5 shrink-0 text-primary" />}
        <div className={urgent ? "text-amber-900" : "text-muted-foreground"}>
          <p className="text-sm" data-testid="credit-footer-balance">
            You have <span className={`font-semibold ${urgent ? "text-amber-900" : "text-foreground"}`}>
              {balance} credit{balance === 1 ? "" : "s"}
            </span> remaining.
          </p>
          {urgent && (
            <p className="mt-0.5 text-xs">Running low — top up so you don't lose momentum.</p>
          )}
        </div>
      </div>
      <Button onClick={() => navigate("/pricing")} data-testid="add-credits-btn"
        size="sm"
        className={`shrink-0 rounded-full gap-1.5 ${
          urgent ? "bg-amber-600 text-white hover:bg-amber-600/90" : ""}`}
        variant={urgent ? "default" : "outline"}>
        <Plus size={15} /> Add More Credits
      </Button>
    </div>
  );
}
